import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatDateTime } from "@/shared/lib/formatters";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { productService } from "@/features/products/services/product-api.service";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { getProductTypeLabel } from "@/features/products/utils/product.utils";
import { getApiErrorMessage } from "@/shared/lib/api-error";

/**
 * Detalhe de um produto do catálogo. Acessível por ADMIN (via menu
 * "Produtos") e por USER (acessado a partir dos itens de beca em um
 * aluguel) — bifurca entre `getById` (admin) e `getByIdUser` (user)
 * conforme o role corrente.
 */
export function ProdutoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [product, setProduct] = useState<ProductResponseDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productId = id ? Number(id) : NaN;
  const isValidId = Number.isInteger(productId) && productId > 0;

  const load = useCallback(async () => {
    if (!isValidId) return;
    setLoading(true);
    setError(null);
    try {
      const data = isAdmin
        ? await productService.getById(productId)
        : await productService.getByIdUser(productId);
      setProduct(data);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, "Erro ao carregar produto");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [productId, isValidId, isAdmin]);

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {product ? `Produto #${product.id}` : "Detalhes do produto"}
          </CardTitle>
          <CardDescription>
            {product
              ? `${product.brand} · ${product.color}`
              : "Carregando informações do produto."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isValidId ? (
            <div className="py-16 text-center text-muted-foreground">
              ID inválido.
            </div>
          ) : loading && !product ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : error && !product ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={load}>
                Tentar novamente
              </Button>
            </div>
          ) : !product ? (
            <div className="py-16 text-center text-muted-foreground">
              Produto não encontrado.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6">
              {product.photoUrl ? (
                <img
                  src={product.photoUrl}
                  alt={product.name ?? product.brand}
                  className="w-full md:w-64 aspect-square rounded-md object-cover border border-border"
                />
              ) : (
                <div className="w-full md:w-64 aspect-square rounded-md bg-muted flex items-center justify-center text-sm text-muted-foreground border border-border">
                  Sem foto
                </div>
              )}

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Tipo</dt>
                  <dd className="text-sm font-medium">
                    {getProductTypeLabel(product.type)}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Marca</dt>
                  <dd className="text-sm font-medium">{product.brand}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Cor</dt>
                  <dd className="text-sm font-medium">{product.color}</dd>
                </div>
                {product.name ? (
                  <div className="space-y-0.5">
                    <dt className="text-xs text-muted-foreground">Nome</dt>
                    <dd className="text-sm font-medium">{product.name}</dd>
                  </div>
                ) : null}
                {product.description ? (
                  <div className="space-y-0.5 sm:col-span-2">
                    <dt className="text-xs text-muted-foreground">Descrição</dt>
                    <dd className="text-sm font-medium whitespace-pre-wrap">
                      {product.description}
                    </dd>
                  </div>
                ) : null}
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">
                    Cadastrado em
                  </dt>
                  <dd className="text-sm font-medium">
                    {formatDateTime(product.createdAt)}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">
                    Atualizado em
                  </dt>
                  <dd className="text-sm font-medium">
                    {formatDateTime(product.updatedAt)}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
